import produce from 'immer';

import * as actionTypes from 'src/app/state/actions/types';
import type { Dispatch, RootState } from 'src/app/state/store';
import type { MedicationDetails } from 'src/app/types';
import { PrescriptionStatuses, PrescriptionStatusTypes } from 'src/app/types';

interface FetchMedicationDetails {
  prescriptionId: string;
  accountId: string;
  isTransfer: boolean;
  useLocalRequestRefillReceivedState: boolean;
  useLocalProcessRefillReceivedState: boolean;
}

export const fetchMedicationDetails = ({
  prescriptionId,
  accountId,
  isTransfer,
  useLocalRequestRefillReceivedState,
  useLocalProcessRefillReceivedState,
}: FetchMedicationDetails) => {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;

    dispatch({ type: actionTypes.GET_MEDICATION_DETAILS_REQUEST });

    try {
      // getTransferDetails returns the same payload as getMedicationDetails
      // but under the hood in the bff calls a bunch less APIs that aren't
      // needed for transfers so it's more efficient.
      const medicationDetails: { data: MedicationDetails } = isTransfer
        ? await client.getTransferDetails(prescriptionId, accountId)
        : await client.getMedicationDetails(prescriptionId, accountId);
      const medicationDetailsData = medicationDetails?.data;
      if (useLocalRequestRefillReceivedState) {
        const medicationDetailsWithLocalRequestReceivedState = produce(medicationDetailsData, draft => {
          draft.prescription.statusText = PrescriptionStatuses.refillRequestReceived;
          draft.prescription.refillStatus.status = PrescriptionStatuses.refillRequestReceived;
          draft.prescription.refillStatusText = PrescriptionStatusTypes.refillRequestReceived;
          draft.prescription.cta = null;
        });
        dispatch({
          type: actionTypes.GET_MEDICATION_DETAILS_SUCCESS,
          prescriptionId,
          payload: medicationDetailsWithLocalRequestReceivedState,
        });
      } else if (useLocalProcessRefillReceivedState) {
        const medicationDetailsWithLocalRequestReceivedState = produce(medicationDetailsData, draft => {
          draft.prescription.statusText = PrescriptionStatuses.processRefillRequestReceived;
          draft.prescription.refillStatus.status = PrescriptionStatuses.processRefillRequestReceived;
          draft.prescription.refillStatusText = PrescriptionStatusTypes.refillRequestReceived;
          draft.prescription.cta = null;
        });
        dispatch({
          type: actionTypes.GET_MEDICATION_DETAILS_SUCCESS,
          prescriptionId,
          payload: medicationDetailsWithLocalRequestReceivedState,
        });
      } else {
        dispatch({
          type: actionTypes.GET_MEDICATION_DETAILS_SUCCESS,
          prescriptionId,
          payload: medicationDetails.data,
        });
      }
    } catch (err) {
      dispatch({ type: actionTypes.GET_MEDICATION_DETAILS_FAILURE, error: err });
    }
  };
};

export const setCurrentPrescriptionId = (prescriptionId: string) => ({
  type: actionTypes.SET_CURRENT_PRESCRIPTION_ID,
  prescriptionId,
});

export const clearMedicationDetailsError = () => ({
  type: actionTypes.CLEAR_MEDICATION_DETAILS_ERROR,
});
